import type { LucideIcon } from 'lucide-react';
import { IconBadge } from '@monksflow/monks-ui';
import clsx from 'clsx';

import type { BrandXRayDto } from '@/entities/analysis';
import { AnalysisMarkdown } from '@/pages/AnalysisResult/components';

export interface CulturalTruth {
  key: keyof BrandXRayDto;
  titleKey: string;
  icon: LucideIcon;
  /**
   * Columns the card claims in the six-column bento. The widths are the design's own and
   * do not track the content: a card keeps its width whether it holds prose or bullets.
   */
  span: 2 | 3 | 6;
  /**
   * The elephant is the one card the design fills, to single it out from its neighbours.
   * Its dark text is neutral-100, the design's accent-foreground; Tessra's is neutral-300.
   */
  filled?: boolean;
}

const SPAN_CLASS: Record<2 | 3 | 6, string> = {
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  6: 'lg:col-span-6',
};

interface CulturalTruthCardProps {
  config: CulturalTruth;
  title: string;
  content: BrandXRayDto[keyof BrandXRayDto];
}

/**
 * One tile of the Cultural Truths bento. The DTO returns either prose or a bullet list per
 * field, so the card renders whichever it was handed.
 */
export const CulturalTruthCard = ({ config, title, content }: CulturalTruthCardProps) => {
  const { filled } = config;
  const bullets = Array.isArray(content) ? content : undefined;
  const body = typeof content === 'string' ? content : undefined;

  return (
    <div
      className={clsx(
        'flex flex-col gap-3 rounded-lg p-xl',
        SPAN_CLASS[config.span],
        filled
          ? 'bg-accent text-accent-foreground dark:text-neutral-100'
          : 'border border-border-light',
      )}
    >
      <div className="flex flex-col items-start gap-3">
        <IconBadge variant="secondary">
          <config.icon />
        </IconBadge>
        <h3
          className={clsx(
            'min-w-0 text-body-xl font-medium',
            filled ? 'text-accent-foreground dark:text-neutral-100' : 'text-foreground',
          )}
        >
          {title}
        </h3>
      </div>

      {body && (
        <AnalysisMarkdown
          text={body}
          className={clsx(
            'max-w-[62ch] text-body-md',
            filled ? 'text-accent-foreground dark:text-neutral-100' : 'text-foreground-alt',
          )}
        />
      )}

      {bullets && (
        <ul className="flex flex-col gap-3">
          {bullets.map((item, i) => (
            <li key={`${item}-${i}`} className="flex items-start gap-3.5 pl-1.5">
              <span
                className={clsx(
                  'mt-[9px] size-1.5 shrink-0 rounded-full opacity-60',
                  filled ? 'bg-accent-foreground dark:bg-neutral-100' : 'bg-foreground-alt',
                )}
              />
              <AnalysisMarkdown
                text={item}
                className={clsx(
                  'text-body-md',
                  filled ? 'text-accent-foreground dark:text-neutral-100' : 'text-foreground-alt',
                )}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
